import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import naijaStateLocalGovernment from "naija-state-local-government";

const CreateCacAccountModal = ({ isOpen, setModal, userId, userEmail }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [draftId, setDraftId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  // Form Fields State
  const [formValues, setFormValues] = useState({
    name: "",
    businessName: "",
    email: "",
    businessEmail: "",
    phoneNo: "",
    businessPhoneNo: "",
    address: "",
    postalAddress: "",
    tradeName: "",
    industrialSector: "",
    businessType: "",
    businessNature: "",
    webAddress: "",
    taxIDNo: "",
    cacNumber: "",
    registrationNumber: "",
    companyRegDate: "",
    dateIncorporated: "",
    businessCommencementDate: "",
    contactPersonName: "",
    referral: "",
    primaryAccount: true,

    // Director 1 (Mandatory)
    director1Bvn: "",
    director1Name: "",
    director1Dob: "",
    director1Nin: "",
    director1PhoneNumber: "",
    director1Email: "",
    director1StreetAddress: "",
    director1City: "",
    director1LocalGovernment: "",
    director1State: "",
    director1Country: "Nigeria",

    // Director 2 (Optional)
    director2Bvn: "",
    director2Name: "",
    director2Dob: "",
    director2Nin: "",
    director2PhoneNumber: "",
    director2Email: "",
    director2StreetAddress: "",
    director2City: "",
    director2LocalGovernment: "",
    director2State: "",
    director2Country: "Nigeria",

    // Shareholder 1 (Optional)
    shareholder1Bvn: "",
    shareholder1Name: "",
    shareholder1Dob: "",
    shareholder1Nin: "",
    shareholder1PhoneNumber: "",
    shareholder1Email: "",
    shareholder1StreetAddress: "",
    shareholder1City: "",
    shareholder1LocalGovernment: "",
    shareholder1State: "",
    shareholder1Country: "Nigeria",

    // Shareholder 2 (Optional)
    shareholder2Bvn: "",
    shareholder2Name: "",
    shareholder2Dob: "",
    shareholder2Nin: "",
    shareholder2PhoneNumber: "",
    shareholder2Email: "",
    shareholder2StreetAddress: "",
    shareholder2City: "",
    shareholder2LocalGovernment: "",
    shareholder2State: "",
    shareholder2Country: "Nigeria",

    // Uploaded Document URLs
    cacCertificateUrl: "",
    memartUrl: "",
    businessUtilityBillUrl: "",
    cacStatusReportUrl: "",

    director1PhotographUrl: "",
    director1UtilityBillUrl: "",
    director1NinUrl: "",

    director2PhotographUrl: "",
    director2UtilityBillUrl: "",
    director2NinUrl: "",

    shareholder1PhotographUrl: "",
    shareholder1UtilityBillUrl: "",
    shareholder1NinUrl: "",

    shareholder2PhotographUrl: "",
    shareholder2UtilityBillUrl: "",
    shareholder2NinUrl: "",
  });

  // BVN Verification States for each persona
  const [bvnVerifyState, setBvnVerifyState] = useState({
    director1: { open: false, bvn: "", dob: "", step: "input", token: "", verifying: false, otp: "" },
    director2: { open: false, bvn: "", dob: "", step: "input", token: "", verifying: false, otp: "" },
    shareholder1: { open: false, bvn: "", dob: "", step: "input", token: "", verifying: false, otp: "" },
    shareholder2: { open: false, bvn: "", dob: "", step: "input", token: "", verifying: false, otp: "" },
  });

  // Fetch draft on mount or when userId changes
  useEffect(() => {
    if (isOpen && userId) {
      fetchDraft();
    }
  }, [isOpen, userId]);

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get(
        `/auth-serviceV3/api/v1/cac-business-accounts/user/${userId}/draft`
      );
      if (response?.data?.success && response?.data?.data) {
        const draft = response.data.data;
        setDraftId(draft.id);

        // Prefill form values with draft values safely
        setFormValues((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            if (draft[key] !== undefined && draft[key] !== null) {
              updated[key] = draft[key];
            }
          });
          return updated;
        });
      }
    } catch (error) {
      console.log("No existing draft or error loading draft:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Document upload handler
  const handleFileUpload = async (event, fieldName, docType) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingField(fieldName);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // Use Waya staging files upload API
      const response = await axiosPrivate.post(
        "/auth-serviceV3/files/upload",
        formData,
        {
          params: {
            documentType: docType,
            username: userEmail || "cac_upload@wayabank.ng",
            folderName: "CAC_Kyc",
          },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      let fileUrl = "";
      if (response?.data?.data && typeof response.data.data === "object") {
        fileUrl = response.data.data.fileUrl || "";
      } else {
        fileUrl = response?.data?.data || response?.data?.url || response?.data?.fileUrl || "";
      }
      if (fileUrl) {
        setFormValues((prev) => ({ ...prev, [fieldName]: fileUrl }));
        success(`${docType} uploaded successfully.`);
      } else {
        snackError("Failed to obtain file URL from response.");
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "File upload failed";
      snackError(errMsg);
    } finally {
      setUploadingField(null);
    }
  };

  // BVN verify helpers
  const handleOpenBvnVerify = (key) => {
    setBvnVerifyState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        open: true,
        step: "input",
        bvn: formValues[`${key}Bvn`] || "",
        dob: formValues[`${key}Dob`] || "",
        token: "",
        otp: ""
      },
    }));
  };

  const handleCloseBvnVerify = (key) => {
    setBvnVerifyState((prev) => ({
      ...prev,
      [key]: { ...prev[key], open: false },
    }));
  };

  const handleBvnInputChange = (key, field, value) => {
    setBvnVerifyState((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const verifyBvnCode = async (key) => {
    const state = bvnVerifyState[key];
    if (!state.bvn || !state.dob) {
      snackError("Please enter both BVN and Date of Birth");
      return;
    }

    setBvnVerifyState((prev) => ({
      ...prev,
      [key]: { ...prev[key], verifying: true },
    }));

    try {
      const response = await axiosPrivate.post("/auth-serviceV3/auth/bvn/verify", {
        bvn: state.bvn,
        dateOfBirth: state.dob,
        processType: "BASIC",
      });

      if (response?.data?.success) {
        const data = response.data.data;
        if (data?.otpVerificationToken) {
          // Requires OTP
          setBvnVerifyState((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              verifying: false,
              step: "otp",
              token: data.otpVerificationToken,
            },
          }));
        } else {
          // Prefill directly if returned
          prefillBvnDetails(key, data);
          setBvnVerifyState((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              verifying: false,
              verifiedData: data,
            },
          }));
          success("BVN verification successful.");
          handleCloseBvnVerify(key);
        }
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "BVN verification failed";
      snackError(errMsg);
      setBvnVerifyState((prev) => ({
        ...prev,
        [key]: { ...prev[key], verifying: false },
      }));
    }
  };

  const verifyOtpCode = async (key) => {
    const state = bvnVerifyState[key];
    if (!state.otp) {
      snackError("Please enter OTP code");
      return;
    }

    setBvnVerifyState((prev) => ({
      ...prev,
      [key]: { ...prev[key], verifying: true },
    }));

    try {
      const response = await axiosPrivate.post("/auth-serviceV3/auth/bvn/verify-otp", {
        otp: state.otp,
        otpVerificationToken: state.token,
      });

      if (response?.data?.success) {
        const data = response.data.data;
        prefillBvnDetails(key, data);
        setBvnVerifyState((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            verifying: false,
            verifiedData: data,
          },
        }));
        success("BVN verification successful.");
        handleCloseBvnVerify(key);
      }
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "OTP verification failed";
      snackError(errMsg);
      setBvnVerifyState((prev) => ({
        ...prev,
        [key]: { ...prev[key], verifying: false },
      }));
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    let p = phone.trim();
    if (p.startsWith("0")) {
      return "+234" + p.slice(1);
    }
    if (!p.startsWith("+")) {
      return "+" + p;
    }
    return p;
  };

  const prefillBvnDetails = (key, data) => {
    if (!data) return;
    const fullName = `${data.firstName || ""} ${data.middleName || ""} ${data.lastName || ""}`.replace(/\s+/g, " ").trim();

    // Format DOB to YYYY-MM-DD safely
    let formattedDob = "";
    if (data.dateOfBirth) {
      try {
        const parts = data.dateOfBirth.split("-");
        if (parts.length === 3) {
          // check if parts are DD-MM-YYYY
          if (parts[2].length === 4) {
            formattedDob = `${parts[2]}-${parts[1]}-${parts[0]}`;
          } else {
            formattedDob = data.dateOfBirth;
          }
        } else {
          formattedDob = new Date(data.dateOfBirth).toISOString().split("T")[0];
        }
      } catch (e) {
        formattedDob = data.dateOfBirth;
      }
    }

    setFormValues((prev) => ({
      ...prev,
      [`${key}Bvn`]: stateToBvn(key),
      [`${key}Name`]: fullName,
      [`${key}Dob`]: formattedDob || prev[`${key}Dob`],
      [`${key}PhoneNumber`]: formatPhone(data.phoneNumber1) || prev[`${key}PhoneNumber`],
      [`${key}StreetAddress`]: data.residentialAddress || prev[`${key}StreetAddress`],
      [`${key}City`]: data.lgaOfResidence || prev[`${key}City`],
      [`${key}LocalGovernment`]: data.lgaOfResidence || prev[`${key}LocalGovernment`],
      [`${key}State`]: data.stateOfOrigin || data.stateOfResidence || prev[`${key}State`],
      [`${key}Country`]: data.nationality || prev[`${key}Country`],
    }));
  };

  const stateToBvn = (key) => {
    return bvnVerifyState[key]?.bvn || "";
  };

  const renderVerifiedBvnCard = (key) => {
    const data = bvnVerifyState[key]?.verifiedData;
    if (!data) return null;

    const imgSrc = data.base64Image
      ? (data.base64Image.startsWith("data:") ? data.base64Image : `data:image/jpeg;base64,${data.base64Image}`)
      : null;

    const fullName = `${data.firstName || ""} ${data.middleName || ""} ${data.lastName || ""}`.replace(/\s+/g, " ").trim();

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: `1px solid ${colors.greenAccent[600]}`,
          borderRadius: "8px",
          bgcolor: colors.primary[400],
          display: "flex",
          gap: "20px",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "flex-start",
        }}
      >
        {imgSrc && (
          <Box
            sx={{
              width: "100px",
              height: "100px",
              flexShrink: 0,
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: colors.primary[300],
            }}
          >
            <img
              src={imgSrc}
              alt="BVN Portrait"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        )}

        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="12px" flex={1}>
          <Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: "block" }}>Full Name</Typography>
            <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>{fullName || "N/A"}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: "block" }}>BVN</Typography>
            <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>{data.bvn || "N/A"}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: "block" }}>Date of Birth</Typography>
            <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>{data.dateOfBirth || "N/A"}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: "block" }}>Phone Number</Typography>
            <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>{data.phoneNumber1 || "N/A"}</Typography>
          </Box>
          <Box sx={{ gridColumn: "span 2" }}>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: "block" }}>Residential Address</Typography>
            <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>{data.residentialAddress || "N/A"}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const cleanPayload = (values) => {
    const cleaned = {};
    Object.keys(values).forEach((key) => {
      const val = values[key];
      if (val !== null && val !== undefined && val !== "") {
        if (Array.isArray(val)) {
          if (val.length > 0) {
            cleaned[key] = val;
          }
        } else {
          cleaned[key] = val;
        }
      }
    });
    return cleaned;
  };

  // Save Draft Action
  const handleSaveDraft = async () => {
    setLoading(true);
    const payload = cleanPayload({
      ...formValues,
      userId,
      businessEmail: formValues.email || formValues.businessEmail,
      // theDirectors array as required by schema description
      theDirectors: [formValues.director1Name, formValues.director2Name].filter(Boolean),
    });

    try {
      let response;
      if (draftId) {
        // Update existing draft
        response = await axiosPrivate.put(
          `/auth-serviceV3/api/v1/cac-business-accounts/${draftId}`,
          payload
        );
      } else {
        // Create new draft
        response = await axiosPrivate.post(
          "/auth-serviceV3/api/v1/cac-business-accounts",
          payload
        );
        if (response?.data?.data?.id) {
          setDraftId(response.data.data.id);
        }
      }

      success("Draft saved successfully.");
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "Failed to save draft";
      snackError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Submit CAC Onboarding
  const handleSubmitCac = async () => {
    if (!formValues.name || !formValues.phoneNo || !formValues.email || !formValues.address) {
      snackError("Please fill out all mandatory Business fields (Name, Phone, Email, Address)");
      return;
    }

    if (!formValues.director1Name || !formValues.director1Bvn) {
      snackError("Director 1 (Mandatory) Name and BVN are required.");
      return;
    }

    setLoading(true);
    try {
      // First save current values to draft
      const payload = cleanPayload({
        ...formValues,
        userId,
        businessEmail: formValues.email || formValues.businessEmail,
        theDirectors: [formValues.director1Name, formValues.director2Name].filter(Boolean),
      });

      let currentDraftId = draftId;
      if (currentDraftId) {
        await axiosPrivate.put(
          `/auth-serviceV3/api/v1/cac-business-accounts/${currentDraftId}`,
          payload
        );
      } else {
        const response = await axiosPrivate.post(
          "/auth-serviceV3/api/v1/cac-business-accounts",
          payload
        );
        currentDraftId = response?.data?.data?.id;
        setDraftId(currentDraftId);
      }

      if (!currentDraftId) {
        throw new Error("Unable to retrieve draft ID to submit.");
      }

      // Submit
      const submitResponse = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/cac-business-accounts/${currentDraftId}/submit`
      );

      success("CAC Corporate Onboarding submitted successfully!");
      setModal(false);
    } catch (error) {
      console.log(error);
      const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || "Failed to submit CAC onboarding";
      snackError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "85%",
    maxWidth: "1000px",
    maxHeight: "90vh",
    bgcolor: colors.primary[400],
    boxShadow: 24,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const renderBvnVerifyBox = (key) => {
    const state = bvnVerifyState[key];
    if (!state.open) return null;

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: `1px solid ${colors.orangeAccent[700]}`,
          borderRadius: "5px",
          bgcolor: colors.primary[400],
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Typography variant="h6" color={colors.grey[100]}>
          Verify BVN
        </Typography>
        {state.step === "input" ? (
          <>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap="15px">
              <TextField
                label="BVN"
                variant="filled"
                size="small"
                value={state.bvn}
                onChange={(e) => handleBvnInputChange(key, "bvn", e.target.value)}
                inputProps={{ maxLength: 11 }}
              />
              <TextField
                label="Date of Birth"
                type="date"
                variant="filled"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={state.dob}
                onChange={(e) => handleBvnInputChange(key, "dob", e.target.value)}
              />
            </Box>
            <Box display="flex" gap="10px" justifyContent="flex-end" mt={1}>
              <Button size="small" color="error" variant="outlined" onClick={() => handleCloseBvnVerify(key)}>
                Cancel
              </Button>
              <Button
                size="small"
                color="secondary"
                variant="contained"
                disabled={state.verifying || state.bvn.length !== 11 || !state.dob}
                onClick={() => verifyBvnCode(key)}
              >
                {state.verifying ? <CircularProgress size={20} /> : "Verify"}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" color={colors.grey[200]}>
              Enter OTP sent to phone number associated with BVN
            </Typography>
            <TextField
              label="OTP"
              variant="filled"
              size="small"
              value={state.otp}
              onChange={(e) => handleBvnInputChange(key, "otp", e.target.value)}
            />
            <Box display="flex" gap="10px" justifyContent="flex-end" mt={1}>
              <Button size="small" color="error" variant="outlined" onClick={() => handleCloseBvnVerify(key)}>
                Cancel
              </Button>
              <Button
                size="small"
                color="secondary"
                variant="contained"
                disabled={state.verifying || !state.otp}
                onClick={() => verifyOtpCode(key)}
              >
                {state.verifying ? <CircularProgress size={20} /> : "Submit OTP"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderFileInput = (fieldName, label, docType) => {
    const isUploading = uploadingField === fieldName;
    const isUploaded = formValues[fieldName];

    return (
      <Box
        sx={{
          border: `1px dashed ${isUploaded ? colors.greenAccent[500] : colors.grey[600]}`,
          borderRadius: "5px",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          bgcolor: colors.primary[400],
          textAlign: "center",
          position: "relative",
          minHeight: "120px",
        }}
      >
        <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>
          {label}
        </Typography>

        {isUploaded ? (
          <Box display="flex" alignItems="center" gap="5px">
            <CheckCircleIcon color="success" />
            <Typography variant="caption" color={colors.greenAccent[400]}>
              Uploaded
            </Typography>
          </Box>
        ) : null}

        <Button
          variant="outlined"
          component="label"
          color="secondary"
          startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          disabled={isUploading}
          size="small"
        >
          {isUploading ? "Uploading..." : "Upload File"}
          <input
            type="file"
            hidden
            onChange={(e) => handleFileUpload(e, fieldName, docType)}
          />
        </Button>

        {isUploaded && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: colors.grey[400],
            }}
          >
            {formValues[fieldName]}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Modal open={isOpen} onClose={() => setModal(false)}>
      <Box sx={style}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          sx={{ borderBottom: `1px solid ${colors.grey[700]}` }}
        >
          <Typography variant="h3" fontWeight="bold" color={colors.grey[100]}>
            Create CAC Corporate Account
          </Typography>
          <IconButton onClick={() => setModal(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs navigation */}
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
          sx={{
            borderBottom: `1px solid ${colors.grey[700]}`,
            "& .MuiTab-root": {
              color: colors.grey[300],
            },
            "& .Mui-selected": {
              color: `${colors.orangeAccent[500]} !important`,
              fontWeight: "bold",
            }
          }}
        >
          <Tab label="Business Info" />
          <Tab label="Directors" />
          <Tab label="Shareholders" />
          <Tab label="Documents Upload" />
        </Tabs>

        {/* Form Body */}
        <Box sx={{ p: 4, flex: 1, overflowY: "auto" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <>
              {/* TAB 0: Business Info */}
              {activeTab === 0 && (
                <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Account Name *"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Name *"
                    name="businessName"
                    value={formValues.businessName}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Email *"
                    name="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Phone Number *"
                    name="phoneNo"
                    value={formValues.phoneNo}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Phone Number"
                    name="businessPhoneNo"
                    value={formValues.businessPhoneNo}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Trade Name"
                    name="tradeName"
                    value={formValues.tradeName}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Postal Address"
                    name="postalAddress"
                    value={formValues.postalAddress}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Address *"
                    name="address"
                    value={formValues.address}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Industrial Sector"
                    name="industrialSector"
                    value={formValues.industrialSector}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Type"
                    name="businessType"
                    value={formValues.businessType}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Business Nature"
                    name="businessNature"
                    value={formValues.businessNature}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Web Address"
                    name="webAddress"
                    value={formValues.webAddress}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Tax ID Number"
                    name="taxIDNo"
                    value={formValues.taxIDNo}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="CAC Number"
                    name="cacNumber"
                    value={formValues.cacNumber}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Registration Number"
                    name="registrationNumber"
                    value={formValues.registrationNumber}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Company Reg Date"
                    name="companyRegDate"
                    InputLabelProps={{ shrink: true }}
                    value={formValues.companyRegDate}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Date Incorporated"
                    name="dateIncorporated"
                    InputLabelProps={{ shrink: true }}
                    value={formValues.dateIncorporated}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Business Commencement Date"
                    name="businessCommencementDate"
                    InputLabelProps={{ shrink: true }}
                    value={formValues.businessCommencementDate}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Contact Person Name"
                    name="contactPersonName"
                    value={formValues.contactPersonName}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Referral Code"
                    name="referral"
                    value={formValues.referral}
                    onChange={handleInputChange}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              )}

              {/* TAB 1: Directors */}
              {activeTab === 1 && (
                <Box display="flex" flexDirection="column" gap="20px">
                  {/* Director 1 (Mandatory) */}
                  <Accordion defaultExpanded sx={{ bgcolor: "transparent", backgroundImage: "none", border: `1px solid ${colors.grey[700]}`, boxShadow: "none" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[100] }} />}>
                      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                        Director 1 (Mandatory)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                        <Box sx={{ gridColumn: "span 4", display: "flex", gap: "10px", alignItems: "center" }}>
                          <TextField
                            fullWidth
                            variant="filled"
                            label="BVN *"
                            name="director1Bvn"
                            value={formValues.director1Bvn}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 11 }}
                          />
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenBvnVerify("director1")}
                          >
                            Verify
                          </Button>
                        </Box>

                        <Box sx={{ gridColumn: "span 4" }}>
                          {renderBvnVerifyBox("director1")}
                          {renderVerifiedBvnCard("director1")}
                        </Box>

                        <TextField
                          fullWidth
                          variant="filled"
                          label="Full Name *"
                          name="director1Name"
                          value={formValues.director1Name}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="date"
                          label="Date of Birth"
                          name="director1Dob"
                          InputLabelProps={{ shrink: true }}
                          value={formValues.director1Dob}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="NIN"
                          name="director1Nin"
                          value={formValues.director1Nin}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Phone Number"
                          name="director1PhoneNumber"
                          value={formValues.director1PhoneNumber}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Email Address"
                          name="director1Email"
                          value={formValues.director1Email}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Street Address"
                          name="director1StreetAddress"
                          value={formValues.director1StreetAddress}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="City"
                          name="director1City"
                          value={formValues.director1City}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Local Government"
                          name="director1LocalGovernment"
                          value={formValues.director1LocalGovernment}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="State"
                          name="director1State"
                          value={formValues.director1State}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Country"
                          name="director1Country"
                          value={formValues.director1Country}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Director 2 (Optional) */}
                  <Accordion sx={{ bgcolor: "transparent", backgroundImage: "none", border: `1px solid ${colors.grey[700]}`, boxShadow: "none", mt: "15px" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[100] }} />}>
                      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                        Director 2 (Optional)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                        <Box sx={{ gridColumn: "span 4", display: "flex", gap: "10px", alignItems: "center" }}>
                          <TextField
                            fullWidth
                            variant="filled"
                            label="BVN"
                            name="director2Bvn"
                            value={formValues.director2Bvn}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 11 }}
                          />
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenBvnVerify("director2")}
                          >
                            Verify
                          </Button>
                        </Box>

                        <Box sx={{ gridColumn: "span 4" }}>
                          {renderBvnVerifyBox("director2")}
                          {renderVerifiedBvnCard("director2")}
                        </Box>

                        <TextField
                          fullWidth
                          variant="filled"
                          label="Full Name"
                          name="director2Name"
                          value={formValues.director2Name}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="date"
                          label="Date of Birth"
                          name="director2Dob"
                          InputLabelProps={{ shrink: true }}
                          value={formValues.director2Dob}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="NIN"
                          name="director2Nin"
                          value={formValues.director2Nin}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Phone Number"
                          name="director2PhoneNumber"
                          value={formValues.director2PhoneNumber}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Email Address"
                          name="director2Email"
                          value={formValues.director2Email}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Street Address"
                          name="director2StreetAddress"
                          value={formValues.director2StreetAddress}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="City"
                          name="director2City"
                          value={formValues.director2City}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Local Government"
                          name="director2LocalGovernment"
                          value={formValues.director2LocalGovernment}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="State"
                          name="director2State"
                          value={formValues.director2State}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Country"
                          name="director2Country"
                          value={formValues.director2Country}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              {/* TAB 2: Shareholders */}
              {activeTab === 2 && (
                <Box display="flex" flexDirection="column" gap="20px">
                  {/* Shareholder 1 (Optional) */}
                  <Accordion sx={{ bgcolor: "transparent", backgroundImage: "none", border: `1px solid ${colors.grey[700]}`, boxShadow: "none" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[100] }} />}>
                      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                        Shareholder 1 (Optional)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                        <Box sx={{ gridColumn: "span 4", display: "flex", gap: "10px", alignItems: "center" }}>
                          <TextField
                            fullWidth
                            variant="filled"
                            label="BVN"
                            name="shareholder1Bvn"
                            value={formValues.shareholder1Bvn}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 11 }}
                          />
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenBvnVerify("shareholder1")}
                          >
                            Verify
                          </Button>
                        </Box>

                        <Box sx={{ gridColumn: "span 4" }}>
                          {renderBvnVerifyBox("shareholder1")}
                          {renderVerifiedBvnCard("shareholder1")}
                        </Box>

                        <TextField
                          fullWidth
                          variant="filled"
                          label="Full Name"
                          name="shareholder1Name"
                          value={formValues.shareholder1Name}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="date"
                          label="Date of Birth"
                          name="shareholder1Dob"
                          InputLabelProps={{ shrink: true }}
                          value={formValues.shareholder1Dob}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="NIN"
                          name="shareholder1Nin"
                          value={formValues.shareholder1Nin}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Phone Number"
                          name="shareholder1PhoneNumber"
                          value={formValues.shareholder1PhoneNumber}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Email Address"
                          name="shareholder1Email"
                          value={formValues.shareholder1Email}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Street Address"
                          name="shareholder1StreetAddress"
                          value={formValues.shareholder1StreetAddress}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="City"
                          name="shareholder1City"
                          value={formValues.shareholder1City}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Local Government"
                          name="shareholder1LocalGovernment"
                          value={formValues.shareholder1LocalGovernment}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="State"
                          name="shareholder1State"
                          value={formValues.shareholder1State}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Country"
                          name="shareholder1Country"
                          value={formValues.shareholder1Country}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Shareholder 2 (Optional) */}
                  <Accordion sx={{ bgcolor: "transparent", backgroundImage: "none", border: `1px solid ${colors.grey[700]}`, boxShadow: "none", mt: "15px" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[100] }} />}>
                      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                        Shareholder 2 (Optional)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                        <Box sx={{ gridColumn: "span 4", display: "flex", gap: "10px", alignItems: "center" }}>
                          <TextField
                            fullWidth
                            variant="filled"
                            label="BVN"
                            name="shareholder2Bvn"
                            value={formValues.shareholder2Bvn}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 11 }}
                          />
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenBvnVerify("shareholder2")}
                          >
                            Verify
                          </Button>
                        </Box>

                        <Box sx={{ gridColumn: "span 4" }}>
                          {renderBvnVerifyBox("shareholder2")}
                          {renderVerifiedBvnCard("shareholder2")}
                        </Box>

                        <TextField
                          fullWidth
                          variant="filled"
                          label="Full Name"
                          name="shareholder2Name"
                          value={formValues.shareholder2Name}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="date"
                          label="Date of Birth"
                          name="shareholder2Dob"
                          InputLabelProps={{ shrink: true }}
                          value={formValues.shareholder2Dob}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="NIN"
                          name="shareholder2Nin"
                          value={formValues.shareholder2Nin}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Phone Number"
                          name="shareholder2PhoneNumber"
                          value={formValues.shareholder2PhoneNumber}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Email Address"
                          name="shareholder2Email"
                          value={formValues.shareholder2Email}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Street Address"
                          name="shareholder2StreetAddress"
                          value={formValues.shareholder2StreetAddress}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="City"
                          name="shareholder2City"
                          value={formValues.shareholder2City}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Local Government"
                          name="shareholder2LocalGovernment"
                          value={formValues.shareholder2LocalGovernment}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="State"
                          name="shareholder2State"
                          value={formValues.shareholder2State}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Country"
                          name="shareholder2Country"
                          value={formValues.shareholder2Country}
                          onChange={handleInputChange}
                          sx={{ gridColumn: "span 2" }}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              {/* TAB 3: Documents Upload */}
              {activeTab === 3 && (
                <Box display="flex" flexDirection="column" gap="30px">
                  <Box>
                    <Typography variant="h5" fontWeight="bold" mb={2} color={colors.grey[100]}>
                      Company Documents
                    </Typography>
                    <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="20px">
                      {renderFileInput("cacCertificateUrl", "CAC Certificate", "CAC_CERTIFICATE")}
                      {renderFileInput("memartUrl", "MEMART", "MEMART")}
                      {renderFileInput("businessUtilityBillUrl", "Business Utility Bill", "BUSINESS_UTILITY_BILL")}
                      {renderFileInput("cacStatusReportUrl", "CAC Status Report", "CAC_STATUS_REPORT")}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="h5" fontWeight="bold" mb={2} color={colors.grey[100]}>
                      Director 1 Documents
                    </Typography>
                    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="20px">
                      {renderFileInput("director1PhotographUrl", "Photograph", "DIRECTOR_1_PHOTO")}
                      {renderFileInput("director1UtilityBillUrl", "Utility Bill", "DIRECTOR_1_UTILITY")}
                      {renderFileInput("director1NinUrl", "NIN Document", "DIRECTOR_1_NIN")}
                    </Box>
                  </Box>

                  {formValues.director2Name && (
                    <Box>
                      <Typography variant="h5" fontWeight="bold" mb={2} color={colors.grey[100]}>
                        Director 2 Documents
                      </Typography>
                      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="20px">
                        {renderFileInput("director2PhotographUrl", "Photograph", "DIRECTOR_2_PHOTO")}
                        {renderFileInput("director2UtilityBillUrl", "Utility Bill", "DIRECTOR_2_UTILITY")}
                        {renderFileInput("director2NinUrl", "NIN Document", "DIRECTOR_2_NIN")}
                      </Box>
                    </Box>
                  )}

                  {formValues.shareholder1Name && (
                    <Box>
                      <Typography variant="h5" fontWeight="bold" mb={2} color={colors.grey[100]}>
                        Shareholder 1 Documents
                      </Typography>
                      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="20px">
                        {renderFileInput("shareholder1PhotographUrl", "Photograph", "SHAREHOLDER_1_PHOTO")}
                        {renderFileInput("shareholder1UtilityBillUrl", "Utility Bill", "SHAREHOLDER_1_UTILITY")}
                        {renderFileInput("shareholder1NinUrl", "NIN Document", "SHAREHOLDER_1_NIN")}
                      </Box>
                    </Box>
                  )}

                  {formValues.shareholder2Name && (
                    <Box>
                      <Typography variant="h5" fontWeight="bold" mb={2} color={colors.grey[100]}>
                        Shareholder 2 Documents
                      </Typography>
                      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="20px">
                        {renderFileInput("shareholder2PhotographUrl", "Photograph", "SHAREHOLDER_2_PHOTO")}
                        {renderFileInput("shareholder2UtilityBillUrl", "Utility Bill", "SHAREHOLDER_2_UTILITY")}
                        {renderFileInput("shareholder2NinUrl", "NIN Document", "SHAREHOLDER_2_NIN")}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          p={3}
          sx={{ borderTop: `1px solid ${colors.grey[700]}`, bgcolor: colors.primary[400] }}
        >
          <Button variant="outlined" color="inherit" onClick={() => setModal(false)}>
            Close
          </Button>
          <Box display="flex" gap="10px">
            <Button
              variant="contained"
              color="warning"
              disabled={loading}
              onClick={handleSaveDraft}
            >
              {loading ? <CircularProgress size={20} /> : "Save Draft"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={loading}
              onClick={handleSubmitCac}
            >
              {loading ? <CircularProgress size={20} /> : "Submit Onboarding"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateCacAccountModal;
